import { StringKey } from '../../../../lang/index';
import { stakeholderTypeTranslation } from '../../../../translations/stakeholder-translation.util';
import { StakeholderType } from '../../../../types/stakeholder.types';

export const GRANTEE = 'grantee' as const;

export type TemplateSignatoryType = StakeholderType | typeof GRANTEE;
export type TemplateSignatoryRole = `${StakeholderType}_${number}` | TemplateSignatoryType;

export const TEMPLATE_SIGNATORY_TYPES: TemplateSignatoryType[] = [
  GRANTEE,
  StakeholderType.ADVISOR,
  StakeholderType.ANGEL,
  StakeholderType.BOARD_MEMBERS,
  StakeholderType.EMPLOYEE,
  StakeholderType.FOUNDER,
  StakeholderType.VCS,
  StakeholderType.INVESTMENT_BANKS,
  StakeholderType.PES,
  StakeholderType.FAMILY_OFFICE,
  StakeholderType.SPV,
  StakeholderType.OTHER_INST,
];

export const templateSignatoryTypeTranslation: Record<TemplateSignatoryType, StringKey> = {
  [GRANTEE]: 'GRANTEE',
  ...stakeholderTypeTranslation,
};

const TEMPLATE_ROLE_ALIAS_PATTERN = /^(.+)_(\d+)$/;

const parseTemplateSignatoryRole = (
  role: string | TemplateSignatoryRole,
): { aliasIndex: null | number; baseRole: string | TemplateSignatoryType } => {
  const match = role.match(TEMPLATE_ROLE_ALIAS_PATTERN);
  if (!match?.[1] || !match[2]) {
    return { baseRole: role, aliasIndex: null };
  }

  const parsedIndex = Number(match[2]);
  const aliasIndex = Number.isInteger(parsedIndex) && parsedIndex > 0 ? parsedIndex : null;

  return { baseRole: match[1], aliasIndex };
};

export const getTemplateSignatoryRoleAliasIndex = (
  role: string | TemplateSignatoryRole,
): null | number => {
  return parseTemplateSignatoryRole(role).aliasIndex;
};

export const getTemplateSignatoryBaseType = (
  role: string | TemplateSignatoryRole,
): TemplateSignatoryType => {
  const { baseRole } = parseTemplateSignatoryRole(role);
  if (baseRole === GRANTEE) {
    return GRANTEE;
  }
  return baseRole as StakeholderType;
};

export const getTemplateSignatoryLabelKey = (
  role: string | TemplateSignatoryRole,
): StringKey | undefined => {
  const baseRole = getTemplateSignatoryBaseType(role);
  return templateSignatoryTypeTranslation[baseRole];
};

export const getTemplateSignatoryBaseDisplayLabel = (
  role: string | TemplateSignatoryRole,
  messagesLocale: Record<string, string>,
): string => {
  const labelKey = getTemplateSignatoryLabelKey(role);
  return labelKey != null ? (messagesLocale[labelKey] ?? role) : role;
};

export const getTemplateSignatoryDisplayLabel = (
  role: string | TemplateSignatoryRole,
  messagesLocale: Record<string, string>,
): string => {
  const baseLabel = getTemplateSignatoryBaseDisplayLabel(role, messagesLocale);
  const aliasIndex = getTemplateSignatoryRoleAliasIndex(role);
  return aliasIndex != null ? `${baseLabel} ${aliasIndex}` : baseLabel;
};
